import { useRouter } from '@bprogress/next';
import { Combobox } from '@headlessui/react';
import { saveSearchQuery } from '@util/firestore/search';
import useNavigationEvent from '@util/hooks/useNavigationEvent';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import { injectWebSiteScript } from 'features/schemaMarkups';
import { newSearchHref } from 'features/search/service/searchHref';
import { useCallback, useRef } from 'react';
import Input from './Input';

interface SearchBoxProps {
  onBlur?: () => void;
  onFocus?: () => void;
  onMobileFocus?: () => void;
  goBack?: () => void;
  onClick?: () => void;
  onEnter?: () => void;
  onClearClick?: () => void;
  searchIconRight?: boolean;
  dismissOnBlur?: boolean;
  autoFocus?: boolean;
  className?: string;
  showSearchPreview?: boolean;
}
const SearchBox = ({
  onBlur,
  onFocus,
  goBack,
  onClick,
  onEnter,
  onClearClick,
  autoFocus,
  onMobileFocus,
  className,
  showSearchPreview,
}: SearchBoxProps) => {
  const {
    searchTerm,
    setSearchTerm,
    setShowSearchPreview,
    setShowMobileSearchbar,
  } = useSearch();

  const { userDoc } = useAuth();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const buildSearchHref = (keyword: string) =>
    newSearchHref(keyword, new URLSearchParams(window.location.search));

  const navigate = (keyword: string) => {
    const href = buildSearchHref(keyword);

    onBlur?.();
    setShowSearchPreview(false);
    return router.push(href);
  };

  useNavigationEvent(
    useCallback(
      ({ url, previousUrl, searchParams, previousSearchParams }) => {
        const handleRouteChange = () => {
          setShowSearchPreview(false);
          setShowMobileSearchbar(false);
        };

        if (
          url !== previousUrl ||
          searchParams?.get('keyword') !== previousSearchParams?.get('word')
        ) {
          handleRouteChange();
        }
      },
      [setShowSearchPreview, setShowMobileSearchbar]
    )
  );

  return (
    <div className="relative !z-[90] mx-auto w-full max-w-[90rem]">
      <script
        id="websiteSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html:
            injectWebSiteScript({
              url: 'https://mxlocker.com/',
              searchUrlTemplate:
                'https://mxlocker.com/search?keyword={search_term_string}',
            }) ?? '',
        }}
      />
      <Combobox
        as="div"
        value={searchTerm}
        onChange={(value) => setSearchTerm(value ?? '')}
        onClick={() => {
          if (isMobile()) {
            setShowSearchPreview(true);
          }
        }}
      >
        <div className="flex items-center gap-x-4">
          <Input
            ref={inputRef}
            goBack={goBack}
            bordered
            full
            autoFocus={autoFocus}
            autoComplete="off"
            id="search-input"
            ariaLabel="navbar search input"
            searchIconRight={true}
            type="search"
            value={searchTerm}
            placeholder="Search for anything"
            showClearIcon={!!searchTerm}
            onClearClick={() => {
              setSearchTerm('');

              if (!isMobile()) {
                setShowSearchPreview(false);
              }

              onClearClick?.();
            }}
            className={`!rounded-[2rem] bg-[#F1F1F1] !p-0 !pl-4 placeholder:font-medium placeholder:text-zinc-400 lg:mt-0 ${className}`}
            iconClassName={'!rounded-[1.75rem] !h-[2rem] w-[2rem]'}
            onClick={() => {
              if (onClick) {
                onClick();
              }
            }}
            onChange={(ev) => {
              setSearchTerm(ev.target.value);
              setShowSearchPreview(!!ev.target.value);
            }}
            onKeyDown={async (ev) => {
              if (ev.key === 'Enter') {
                const keyword = (ev.target as HTMLInputElement).value;

                if (keyword) {
                  // NOTE: is this the best place to put this? would be nice to have some memoization.
                  // Before searching, lets save the search term to the DB so we can log that they searched for something
                  if (userDoc) {
                    await saveSearchQuery({
                      query: searchTerm ?? '',
                      uid: userDoc.uid,
                    });
                  }

                  navigate(keyword);
                  onEnter?.();
                  setShowSearchPreview(false);
                  setShowMobileSearchbar(false);

                  // Blur input to close iOS keyboard
                  if (inputRef.current) {
                    inputRef.current.blur();
                  }
                }
              }
            }}
            onFocus={() => {
              onFocus?.();

              if (onMobileFocus) {
                onMobileFocus();
              }
            }}
            onBlur={() => {
              onBlur?.();
            }}
            onSearchButtonClick={async () => {
              if (searchTerm) {
                if (userDoc) {
                  await saveSearchQuery({
                    query: searchTerm,
                    uid: userDoc.uid,
                  });
                }

                navigate(searchTerm);
                onEnter?.();
                setShowSearchPreview(false);
                setShowMobileSearchbar(false);

                // Blur input to close iOS keyboard
                if (inputRef.current) {
                  inputRef.current.blur();
                }
              }
            }}
          />

          {isMobile() && showSearchPreview && (
            <button
              type="button"
              className="pr-4 text-[1.6rem] font-medium text-zinc-700"
              aria-label="cancel search"
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm('');
                setShowSearchPreview(false);
                onClearClick?.();
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </Combobox>
    </div>
  );
};

export default SearchBox;
